import { useMemo } from 'react';
import { useUser } from '@clerk/clerk-react';
import type { NavNode, NavSection } from '@strivelabs/ui-blocks/v2';
import { GUEST_NAV_ITEMS, INTERNAL_NAV_IDS, NAV_ITEMS } from '../data/navItems';
import { STRIVELABS_USERS } from '@/constants';
import { getAgentUrl } from '@/constants/agents';
import { useDemoAgentFiltering } from '@/hooks/useDemoAgentFiltering';
import { useUserRole } from '@/hooks/useUserRole';
import { useAppStore } from '@/store/appStore';
import placeholderAvatar from '@/assets/avatars/placeholder-avatar.png';

/**
 * Builds the sidebar tree for the current user.
 *
 * Memoised on role, flags and the agent list so the sections array keeps a
 * stable identity between renders. Active state is derived inside ui-core from
 * `currentPath`, so this deliberately does not depend on the route.
 */
export function useNavTree(): NavSection[] {
  const { user } = useUser();
  const { isGuest } = useUserRole();
  const agentInstances = useAppStore(state => state.agentInstances);
  const { filterAgentInstances, categorizeAgentInstances } = useDemoAgentFiltering();

  const userEmail = user?.primaryEmailAddress?.emailAddress;
  const isInternal = Boolean(userEmail && STRIVELABS_USERS.includes(userEmail));

  /**
   * Agents grouped by category, shaped as nav sections. Replaces the old
   * WorkforcePopover — the data already lives in the store, so these are static
   * children rather than a lazy fetch.
   */
  const workforceChildren = useMemo<NavSection[] | undefined>(() => {
    const visible = filterAgentInstances(agentInstances);
    if (visible.length === 0) return undefined;

    const byCategory = categorizeAgentInstances(visible);
    const sections = Object.entries(byCategory).flatMap(([category, instances]) => {
      const items: NavNode[] = instances.flatMap(instance => {
        const agent = instance.agent;
        if (!agent?.short_name) return [];
        return [
          {
            id: String(instance.id),
            label: agent.display_name || 'Unnamed Agent',
            href: getAgentUrl(agent.short_name),
            icon: (
              <img
                src={agent.avatar || placeholderAvatar}
                alt=""
                className="size-4 rounded-full object-cover"
              />
            ),
          },
        ];
      });
      return items.length > 0 ? [{ id: `workforce-${category}`, label: category, items }] : [];
    });

    return sections.length > 0 ? sections : undefined;
  }, [agentInstances, filterAgentInstances, categorizeAgentInstances]);

  return useMemo(() => {
    if (isGuest) return GUEST_NAV_ITEMS;

    const isVisible = (item: NavNode) =>
      !(INTERNAL_NAV_IDS as readonly string[]).includes(item.id) || isInternal;

    const withChildren = (item: NavNode) =>
      item.id === 'workforce' && workforceChildren ? { ...item, children: workforceChildren } : item;

    return NAV_ITEMS.flatMap(section => {
      const items = section.items.flatMap(item => (isVisible(item) ? [withChildren(item)] : []));
      // A section whose every item was filtered out would render as a bare
      // heading with nothing under it.
      return items.length > 0 ? [{ ...section, items }] : [];
    });
  }, [isGuest, isInternal, workforceChildren]);
}
